import { Button, Form, Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import emailjs from "@emailjs/browser";
import { EMAILJS } from "../../config/email";
import "./WaitingList.css";

/**
 * Waiting-list request for any speaker. Nothing is charged: the details go to
 * SonicBeats by email and they follow up with a lead time.
 *
 * `product` is what the visitor picked ("Blackbox", "N1-12 Active"). Pages with
 * their own options render them as `children`, which sit next to quantity.
 * `venue` adds the business fields the N1 handoff asks for: company, mounting
 * and an installation quote.
 */
function WaitingListForm({
  product,
  details = [],
  venue = false,
  mountingOptions = [],
  children,
}) {
  const [status, setStatus] = useState("idle");

  const form = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      quantity: 1,
      colour: "",
      mounting: mountingOptions[mountingOptions.length - 1] || "",
      project: "",
      installation: false,
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Please enter your name"),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Please enter your email"),
      phone: Yup.string(),
      company: venue
        ? Yup.string().required("Please enter your company or venue")
        : Yup.string(),
      quantity: Yup.number()
        .typeError("Please enter a number")
        .integer("Please enter a whole number")
        .min(1, "At least one speaker")
        .max(100, "For more than 100, tell us in the message")
        .required("Please enter a quantity"),
      colour: Yup.string().max(200, "Please keep it under 200 characters"),
      project: Yup.string().max(1500, "Please keep it under 1500 characters"),
    }),

    onSubmit: async (values) => {
      const installation = values.installation ? "Yes" : "No";

      // The shared template renders name, email, phone and description, so the
      // whole request is also written out in `description`. The separate fields
      // ride along for when the template gets a waiting-list layout of its own.
      const description = [
        `WAITING LIST — ${product}`,
        `Quantity: ${values.quantity}`,
        ...details,
        venue && `Company / venue: ${values.company}`,
        `Colour preference: ${values.colour || "—"}`,
        venue && `Mounting: ${values.mounting}`,
        venue && `Installation quote: ${installation}`,
        "",
        venue ? "Project:" : "Message:",
        values.project || "—",
      ]
        .filter((line) => line !== false)
        .join("\n");

      setStatus("sending");
      try {
        await emailjs.send(
          EMAILJS.serviceId,
          EMAILJS.templateId,
          {
            // The template greets {{to_name}} and replies to {{reply_to}}.
            to_name: "SonicBeats",
            reply_to: values.email,
            name: values.name,
            email: values.email,
            phone: values.phone || "—",
            description,
            product,
            quantity: values.quantity,
            colour: values.colour,
            company: values.company,
            mounting: venue ? values.mounting : "",
            installation_quote: venue ? installation : "",
            project: values.project,
          },
          EMAILJS.publicKey,
        );
        setStatus("sent");
      } catch (error) {
        console.error("Waiting list request failed:", error);
        setStatus("error");
      }
    },
  });

  const invalid = (field) => form.touched[field] && !!form.errors[field];

  if (status === "sent") {
    return (
      <div className="sb-waitlist-done rounded-4 p-4 p-md-5" role="status">
        <span className="sb-eyebrow">You're on the list</span>
        <h4 className="fw-bold">
          Thank you, {form.values.name.split(" ")[0]}.
        </h4>
        <p className="text-muted mb-0">
          We have your request for {form.values.quantity} × {product}. We will
          contact you at{" "}
          <strong className="text-body">{form.values.email}</strong> with a lead
          time{venue && " and a quote"}.
        </p>
      </div>
    );
  }

  const field = (name, label, props = {}) => (
    <Form.Group className="mb-3" controlId={`waitlist-${name}`}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        name={name}
        value={form.values[name]}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
        isInvalid={invalid(name)}
        {...props}
      />
      <Form.Control.Feedback type="invalid">
        {form.errors[name]}
      </Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Form onSubmit={form.handleSubmit} noValidate>
      <Row className="g-3">
        <Col sm={6}>{field("name", "Name", { autoComplete: "name" })}</Col>
        <Col sm={6}>
          {field("email", "Email", { type: "email", autoComplete: "email" })}
        </Col>
        <Col sm={6}>
          {field("phone", "Phone (optional)", {
            type: "tel",
            autoComplete: "tel",
          })}
        </Col>
        <Col sm={6}>
          {venue
            ? field("company", "Company / venue", {
                autoComplete: "organization",
              })
            : field("quantity", "Quantity", {
                type: "number",
                min: 1,
                max: 100,
                inputMode: "numeric",
              })}
        </Col>

        {venue && (
          <>
            {children}
            <Col sm={4}>
              {field("quantity", "Quantity", {
                type: "number",
                min: 1,
                max: 100,
                inputMode: "numeric",
              })}
            </Col>
          </>
        )}

        <Col sm={venue ? 6 : 12}>
          {field(
            "colour",
            venue ? "Colour preference" : "Colour preference (optional)",
            { placeholder: venue ? "e.g. red grille, black side panels" : "" },
          )}
        </Col>
        {venue && (
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="waitlist-mounting">
              <Form.Label>Mounting</Form.Label>
              <Form.Select
                name="mounting"
                value={form.values.mounting}
                onChange={form.handleChange}
              >
                {mountingOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        )}

        <Col xs={12}>
          {field("project", venue ? "Your project" : "Message (optional)", {
            as: "textarea",
            rows: venue ? 4 : 3,
            placeholder: venue
              ? "The space, how many rooms or zones, timing — anything that helps us quote."
              : "",
          })}
        </Col>
      </Row>

      {venue && (
        <Form.Check
          type="checkbox"
          id="waitlist-installation"
          name="installation"
          checked={form.values.installation}
          onChange={form.handleChange}
          label="I would like a quote for installation"
          className="mb-4"
        />
      )}

      <Button
        variant="primary"
        type="submit"
        className="btn-rounded px-5 py-2 mt-2"
        disabled={status === "sending"}
      >
        {status === "sending" ? "Sending…" : "Join the waiting list"}
      </Button>

      {status === "error" && (
        <div className="alert alert-danger mt-4 rounded-4 mb-0" role="alert">
          That did not go through. Please try again, or email us at{" "}
          <a href="mailto:designed@sonicbeats.nl" className="alert-link">
            designed@sonicbeats.nl
          </a>
          .
        </div>
      )}
    </Form>
  );
}

export default WaitingListForm;
